import React, { useState, useCallback, useMemo } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    Menu,
    MenuItem,
    IconButton,
    Divider,
    Avatar,
    Tooltip,
    useMediaQuery,
    useTheme,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";

// Import necessary icons
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import SchoolIcon from '@mui/icons-material/School';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import TranslateIcon from '@mui/icons-material/Translate';
import ReviewsIcon from '@mui/icons-material/Reviews';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import LoginIcon from '@mui/icons-material/Login';
import ChatIcon from '@mui/icons-material/Chat';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import TourIcon from '@mui/icons-material/Tour';
import { useTourManager } from "./tour/TourManager";

// Import our navigation context
import { useNavigation } from "../context/NavigationContext";
import { getDecodedToken } from "../utils/auth";

// Language mapping - moved outside component to prevent recreation
const languageData = {
    'de': { native: 'Deutsch', flag: 'de' },
    'es': { native: 'Español', flag: 'es' },
    'fr': { native: 'Français', flag: 'fr' },
    'it': { native: 'Italiano', flag: 'it' },
    'pt': { native: 'Português', flag: 'pt' },
    'ja': { native: '日本語', flag: 'jp' },
    'ko': { native: '한국어', flag: 'kr' },
    'zh': { native: '中文', flag: 'cn' },
    'ru': { native: 'Русский', flag: 'ru' },
    'ar': { native: 'العربية', flag: 'sa' },
};

const Navbar = React.memo(({ token, username, handleLogout }) => {
    // FIXED: Use correct destructuring name from NavigationContext
    const { currentCourse, selectedLanguage, clearNavigationContext } = useNavigation();
    
    // Memoize decoded token to prevent recalculation
    const decodedToken = useMemo(() => token ? getDecodedToken() : null, [token]);
    const userEmail = decodedToken?.user?.email;
    
    // Get username from token if not provided as prop (for persistence)
    const displayUsername = useMemo(() => 
        username || decodedToken?.user?.username || decodedToken?.user?.email?.split('@')[0] || 'User',
        [username, decodedToken]
    );

    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // State for dropdowns
    const [languageMenu, setLanguageMenu] = useState(null);
    const [courseMenu, setCourseMenu] = useState(null);
    const [profileMenu, setProfileMenu] = useState(null);
    const [mobileMenu, setMobileMenu] = useState(false);

    // FIXED: Memoize all event handlers with useCallback
    const handleLanguageMenuOpen = useCallback((event) => setLanguageMenu(event.currentTarget), []);
    const handleLanguageMenuClose = useCallback(() => setLanguageMenu(null), []);
    const handleCourseMenuOpen = useCallback((event) => setCourseMenu(event.currentTarget), []);
    const handleCourseMenuClose = useCallback(() => setCourseMenu(null), []);
    const handleProfileMenuOpen = useCallback((event) => setProfileMenu(event.currentTarget), []);
    const handleProfileMenuClose = useCallback(() => setProfileMenu(null), []);
    const toggleMobileMenu = useCallback(() => setMobileMenu(prev => !prev), []);

    // FIXED: Always call the hook, now it's safe for unauthenticated users
    const tourManager = useTourManager();
    const { startLanguageDashboardTour } = tourManager;

    // FIXED: Memoize navigation handlers
    const handleLogoClick = useCallback(() => {
        navigate('/languages');
    }, [navigate]);

    const navigateTo = useCallback((path) => {
        navigate(path);
        setLanguageMenu(null);
        setCourseMenu(null);
        setProfileMenu(null);
        setMobileMenu(false);
    }, [navigate]);

    // FIXED: Enhanced logout handler that clears navigation context
    const handleLogoutClick = useCallback(() => {
        // Clear navigation context before logging out
        if (clearNavigationContext) {
            clearNavigationContext();
        }
        handleLogout();
        setProfileMenu(null);
        setMobileMenu(false);
    }, [clearNavigationContext, handleLogout]);

    // FIXED: Memoize tour handler with safety check
    const handleStartTour = useCallback(() => {
        // Only proceed if user is logged in and tour manager is available
        if (!token || !startLanguageDashboardTour) {
            return;
        }

        setProfileMenu(null);
        setMobileMenu(false);
        
        // Check if we're on the language dashboard page
        const isOnLanguageDashboard = location.pathname.includes('/language-dashboard/');
        
        if (isOnLanguageDashboard) {
            // Start tour immediately if on dashboard
            startLanguageDashboardTour();
        } else if (currentCourse?._id) {
            // Navigate to dashboard first, then start tour
            navigate(`/language-dashboard/${currentCourse._id}`);
            // Tour will auto-start when dashboard loads via TourManagerWrapper
            setTimeout(() => {
                startLanguageDashboardTour();
            }, 1500);
        } else {
            // Navigate to languages page if no current course
            navigate('/languages');
        }
    }, [location.pathname, currentCourse, navigate, startLanguageDashboardTour, token]);

    // FIXED: Memoize utility functions
    const isActive = useCallback((path) => {
        if (path.startsWith('/chat') && location.pathname.startsWith('/chat')) {
            return true;
        }
        return location.pathname === path;
    }, [location.pathname]);

    // FIXED: Memoize computed values
    const getLanguageText = useMemo(() => {
        if (!selectedLanguage) return "Languages";
        return selectedLanguage.name || "Languages";
    }, [selectedLanguage]);

    const getCourseText = useMemo(() => {
        if (!currentCourse) return "Learn";
        return `Learn ${currentCourse.title}`;
    }, [currentCourse]);

    const getCurrentLanguageCode = useMemo(() => {
        if (currentCourse && currentCourse.language) {
            return currentCourse.language;
        }
        if (selectedLanguage && selectedLanguage.code) {
            return selectedLanguage.code;
        }
        return null;
    }, [currentCourse, selectedLanguage]);

    const hasLanguageSelected = useMemo(() => {
        return getCurrentLanguageCode !== null;
    }, [getCurrentLanguageCode]);

    const getCurrentLanguageFlag = useMemo(() => {
        return getCurrentLanguageCode ? languageData[getCurrentLanguageCode]?.flag : null;
    }, [getCurrentLanguageCode]);

    const getUserInitial = useMemo(() => {
        if (displayUsername && displayUsername !== 'User') {
            return displayUsername.charAt(0).toUpperCase();
        }
        return 'U';
    }, [displayUsername]);

    // FIXED: Memoize styles object to prevent recreation
    const navStyles = useMemo(() => ({
        appBar: {
            background: '#f8f9fa',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            color: '#333',
        },
        logo: {
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: isMobile ? '1.2rem' : '1.5rem',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            color: '#333',
        },
        navContainer: {
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#f0f0f0',
            borderRadius: '8px',
            padding: '4px',
            mx: 2
        },
        navLink: {
            color: '#555',
            mx: 2,
            py: 0.8,
            px: 1.8,
            fontWeight: 400,
            fontSize: '0.95rem',
            textTransform: 'none',
            borderRadius: '10px',
            transition: 'all 0.2s',
            '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)',
            },
            '&.Mui-disabled': {
                color: 'rgba(0, 0, 0, 0.26)',
                cursor: 'not-allowed',
            }
        },
        activeLink: {
            backgroundColor: '#000',
            color: '#fff',
            fontWeight: 400,
            py: 0.6,
            px: 1.6,
            borderRadius: '10px',
        },
        paymentButton: {
            backgroundColor: '#000',
            color: '#fff',
            borderRadius: '4px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            py: 0.8,
            px: 2,
            '&:hover': {
                backgroundColor: '#333',
                boxShadow: 'none',
            }
        },
        iconButton: {
            color: '#555',
            mx: 0.5,
        },
        button: {
            color: '#555',
            mx: 1,
            px: 2,
            py: 1,
            borderRadius: '4px',
            textTransform: 'none',
            fontSize: '0.95rem',
            fontWeight: 500,
            transition: 'all 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)',
            }
        },
        dropdownButton: {
            display: 'flex',
            alignItems: 'center',
        },
        menu: {
            mt: '8px',
            borderRadius: '8px',
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
        },
        menuItem: {
            px: 2,
            py: 1.5,
            minWidth: '180px',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            transition: 'background-color 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)',
            },
        },
        avatar: {
            width: 36,
            height: 36,
            backgroundColor: '#1976d2',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
        },
        mobileDrawer: {
            width: 280,
            '& .MuiDrawer-paper': {
                width: 280,
                backgroundColor: '#fff',
            },
        },
        mobileListItem: {
            padding: '12px 24px',
            '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)',
            }
        },
        mobileIcon: {
            minWidth: 40,
            color: '#555',
        },
        divider: {
            backgroundColor: 'rgba(0,0,0,0.1)',
            margin: '8px 0',
        },
        flagImg: {
            width: 20,
            height: 20,
            marginRight: '6px',
            borderRadius: '2px',
        }
    }), [isMobile]);

    // FIXED: Memoize desktop navbar to prevent recreation
    const desktopNavbar = useMemo(() => (
        <>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={handleLogoClick}>
                <Typography variant="h6" sx={navStyles.logo}>
                    <span style={{ fontWeight: 800 }}>ANKIVERSE</span>.com
                </Typography>
            </Box>

            {/* Navigation Links in Container */}
            <Box sx={navStyles.navContainer}>
                {/* Languages - Home */}
                <Button
                    sx={{
                        ...navStyles.navLink,
                        ...(isActive('/languages') && navStyles.activeLink)
                    }}
                    component={Link}
                    to="/languages"
                    startIcon={<TranslateIcon />}
                >
                    {getLanguageText}
                </Button>

                {/* Direct Language Home Button */}
                {hasLanguageSelected && currentCourse && (
                    <Button
                        sx={{
                            ...navStyles.navLink,
                            ...(isActive(`/language-dashboard/${currentCourse._id}`) && navStyles.activeLink),
                        }}
                        onClick={() => navigateTo(`/language-dashboard/${currentCourse._id}`)}
                        startIcon={<HomeIcon />}
                    >
                        Language Home
                    </Button>
                )}

                {/* Learning [Language] dropdown */}
                {hasLanguageSelected && (
                    <Button
                        sx={{
                            ...navStyles.navLink,
                            ...(isActive('/course') && navStyles.activeLink)
                        }}
                        onClick={handleCourseMenuOpen}
                        endIcon={<KeyboardArrowDownIcon />}
                    >
                        {getCourseText}
                    </Button>
                )}
                <Menu
                    anchorEl={courseMenu}
                    open={Boolean(courseMenu)}
                    onClose={handleCourseMenuClose}
                    PaperProps={{
                        sx: navStyles.menu
                    }}
                >
                    {currentCourse && (
                        <>
                            <MenuItem
                                sx={navStyles.menuItem}
                                onClick={() => navigateTo(`/course-learn`)}
                            >
                                <SchoolIcon fontSize="small" />
                                <Typography>Continue Learning</Typography>
                            </MenuItem>
                            <MenuItem
                                sx={navStyles.menuItem}
                                onClick={() => navigateTo(`/review-adapter/${currentCourse.deckId}`)}
                            >
                                <ReviewsIcon fontSize="small" />
                                <Typography>Review Progress</Typography>
                            </MenuItem>
                            <MenuItem
                                sx={navStyles.menuItem}
                                onClick={() => navigateTo(`/view-course/${currentCourse._id}`)}
                            >
                                <PlayCircleIcon fontSize="small" />
                                <Typography>View Course</Typography>
                            </MenuItem>
                        </>
                    )}
                </Menu>

                {/* Chat button - uses the current language */}
                {hasLanguageSelected && (
                    <Button
                        sx={{
                            ...navStyles.navLink,
                            ...(isActive('/chat') && navStyles.activeLink)
                        }}
                        onClick={() => navigateTo(`/chat/${getCurrentLanguageCode}`)}
                        startIcon={
                            getCurrentLanguageFlag ? (
                                <Box
                                    component="img"
                                    src={`https://flagcdn.com/w20/${getCurrentLanguageFlag}.png`}
                                    alt={getCurrentLanguageCode}
                                    sx={navStyles.flagImg}
                                />
                            ) : (
                                <ChatIcon fontSize="small" />
                            )
                        }
                    >
                        Chat
                    </Button>
                )}

                {/* Games */}
                {hasLanguageSelected && (
                    <Button
                        sx={{
                            ...navStyles.navLink,
                            ...(isActive('/games') && navStyles.activeLink)
                        }}
                        component={Link}
                        to="/games"
                        startIcon={<SportsEsportsIcon fontSize="small" />}
                    >
                        Games
                    </Button>
                )}
            </Box>

            {/* Right Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {token ? (
                    <>
                        <Button
                            variant="contained"
                            sx={navStyles.paymentButton}
                            component={Link}
                            to="/upgrade"
                        >
                            Upgrade
                        </Button>

                        <Tooltip title="Account Settings">
                            <IconButton
                                onClick={handleProfileMenuOpen}
                                size="small"
                                sx={{ ml: 1 }}
                                data-tour="user-avatar"
                            >
                                <Avatar sx={navStyles.avatar}>
                                    {getUserInitial}
                                </Avatar>
                            </IconButton>
                        </Tooltip>

                        <Menu
                            anchorEl={profileMenu}
                            open={Boolean(profileMenu)}
                            onClose={handleProfileMenuClose}
                            PaperProps={{
                                sx: navStyles.menu
                            }}
                        >
                            <Box px={2} py={1}>
                                <Typography variant="subtitle2" color="text.primary" fontWeight="medium">
                                    {displayUsername}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {userEmail}
                                </Typography>
                            </Box>
                            <Divider sx={navStyles.divider} />
                            <MenuItem
                                sx={navStyles.menuItem}
                                onClick={() => navigateTo('/settings')}
                            >
                                <SettingsIcon fontSize="small" />
                                <Typography>Settings</Typography>
                            </MenuItem>
                            {/* Only show tour option if user is logged in */}
                            {token && (
                                <MenuItem
                                    sx={navStyles.menuItem}
                                    onClick={handleStartTour}
                                >
                                    <TourIcon fontSize="small" />
                                    <Typography>How to use</Typography>
                                </MenuItem>
                            )}
                            <Divider sx={navStyles.divider} />
                            <MenuItem
                                sx={navStyles.menuItem}
                                onClick={handleLogoutClick}
                            >
                                <ExitToAppIcon fontSize="small" color="error" />
                                <Typography color="error">Logout</Typography>
                            </MenuItem>
                        </Menu>
                    </>
                ) : (
                    <>
                        <Button
                            variant="outlined"
                            sx={{ ...navStyles.button, mr: 1 }}
                            component={Link}
                            to="/login"
                        >
                            Login
                        </Button>
                        <Button
                            variant="contained"
                            sx={navStyles.paymentButton}
                            component={Link}
                            to="/signup"
                        >
                            Sign Up
                        </Button>
                    </>
                )}
            </Box>
        </>
    ), [
        handleLogoClick, navStyles, getLanguageText, isActive, hasLanguageSelected, currentCourse,
        navigateTo, getCourseText, handleCourseMenuOpen, courseMenu, handleCourseMenuClose,
        getCurrentLanguageCode, getCurrentLanguageFlag, token, handleProfileMenuOpen,
        profileMenu, handleProfileMenuClose, displayUsername, userEmail, handleStartTour,
        handleLogoutClick, getUserInitial
    ]);

    // FIXED: Memoize mobile navbar to prevent recreation
    const mobileNavbar = useMemo(() => (
        <>
            <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={toggleMobileMenu}
                sx={{ mr: 2 }}
            >
                <MenuIcon />
            </IconButton>

            <Typography
                variant="h6"
                sx={{ ...navStyles.logo, flexGrow: 1 }}
                onClick={handleLogoClick}
            >
                <span style={{ fontWeight: 800 }}>ANKIVERSE</span>.com
            </Typography>

            {token ? (
                <IconButton 
                    onClick={handleProfileMenuOpen} 
                    sx={{ p: 0, ml: 1 }}
                    data-tour="user-avatar"
                >
                    <Avatar sx={{ ...navStyles.avatar, width: 32, height: 32 }}>
                        {getUserInitial}
                    </Avatar>
                </IconButton>
            ) : (
                <Button
                    variant="contained"
                    sx={{ ...navStyles.paymentButton, py: 0.5 }}
                    component={Link}
                    to="/login"
                >
                    Login
                </Button>
            )}

            <Drawer
                anchor="left"
                open={mobileMenu}
                onClose={toggleMobileMenu}
                sx={navStyles.mobileDrawer}
            >
                <Box p={2} textAlign="center">
                    <Typography variant="h6" sx={navStyles.logo} onClick={handleLogoClick}>
                        <span style={{ fontWeight: 800 }}>ANKIVERSE</span>.com
                    </Typography>
                </Box>
                <Divider sx={navStyles.divider} />

                <List>
                    {token ? (
                        <>
                            {/* Mobile menu items for logged in users */}
                            <ListItem
                                button
                                sx={navStyles.mobileListItem}
                                onClick={() => navigateTo('/languages')}
                            >
                                <ListItemIcon sx={navStyles.mobileIcon}>
                                    <TranslateIcon />
                                </ListItemIcon>
                                <ListItemText primary={getLanguageText} />
                            </ListItem>

                            {hasLanguageSelected && currentCourse && (
                                <ListItem
                                    button
                                    sx={navStyles.mobileListItem}
                                    onClick={() => navigateTo(`/language-dashboard/${currentCourse._id}`)}
                                >
                                    <ListItemIcon sx={navStyles.mobileIcon}>
                                        <HomeIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Language Home" />
                                </ListItem>
                            )}

                            {hasLanguageSelected && currentCourse && (
                                <ListItem
                                    button
                                    sx={navStyles.mobileListItem}
                                    onClick={() => navigateTo(`/course-learn`)}
                                >
                                    <ListItemIcon sx={navStyles.mobileIcon}>
                                        <SchoolIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={`Learn ${currentCourse.title}`} />
                                </ListItem>
                            )}

                            {hasLanguageSelected && (
                                <ListItem
                                    button
                                    sx={navStyles.mobileListItem}
                                    onClick={() => navigateTo(`/chat/${getCurrentLanguageCode}`)}
                                >
                                    <ListItemIcon sx={navStyles.mobileIcon}>
                                        {getCurrentLanguageFlag ? (
                                            <Box
                                                component="img"
                                                src={`https://flagcdn.com/w20/${getCurrentLanguageFlag}.png`}
                                                alt={getCurrentLanguageCode}
                                                sx={{ ...navStyles.flagImg, width: 24, height: 16 }}
                                            />
                                        ) : (
                                            <ChatIcon />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText primary="Language Chat" />
                                </ListItem>
                            )}

                            {hasLanguageSelected && (
                                <ListItem
                                    button
                                    sx={navStyles.mobileListItem}
                                    onClick={() => navigateTo('/games')}
                                >
                                    <ListItemIcon sx={navStyles.mobileIcon}>
                                        <SportsEsportsIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Games" />
                                </ListItem>
                            )}

                            {hasLanguageSelected && currentCourse && (
                                <>
                                    <Divider sx={navStyles.divider} />
                                    <ListItem
                                        button
                                        sx={navStyles.mobileListItem}
                                        onClick={() => navigateTo(`/review-adapter/${currentCourse.deckId}`)}
                                    >
                                        <ListItemIcon sx={navStyles.mobileIcon}>
                                            <ReviewsIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Review Progress" />
                                    </ListItem>

                                    <ListItem
                                        button
                                        sx={navStyles.mobileListItem}
                                        onClick={() => navigateTo(`/view-course/${currentCourse._id}`)}
                                    >
                                        <ListItemIcon sx={navStyles.mobileIcon}>
                                            <PlayCircleIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="View Course" />
                                    </ListItem>
                                </>
                            )}

                            <Divider sx={navStyles.divider} />

                            {displayUsername === 'admin' && (
                                <ListItem
                                    button
                                    sx={navStyles.mobileListItem}
                                    onClick={() => navigateTo('/admin-dashboard')}
                                >
                                    <ListItemIcon sx={navStyles.mobileIcon}>
                                        <AdminPanelSettingsIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Admin Console" />
                                </ListItem>
                            )}

                            <ListItem
                                button
                                sx={navStyles.mobileListItem}
                                onClick={() => navigateTo('/settings')}
                            >
                                <ListItemIcon sx={navStyles.mobileIcon}>
                                    <SettingsIcon />
                                </ListItemIcon>
                                <ListItemText primary="Settings" />
                            </ListItem>

                            {/* Only show tour option if user is logged in */}
                            {token && (
                                <ListItem
                                    button
                                    sx={navStyles.mobileListItem}
                                    onClick={handleStartTour}
                                >
                                    <ListItemIcon sx={navStyles.mobileIcon}>
                                        <TourIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="How to use" />
                                </ListItem>
                            )}

                            <ListItem
                                button
                                sx={navStyles.mobileListItem}
                                onClick={() => navigateTo('/upgrade')}
                            >
                                <ListItemIcon sx={navStyles.mobileIcon}>
                                    <AccountCircleIcon />
                                </ListItemIcon>
                                <ListItemText primary="Upgrade" />
                            </ListItem>

                            <Divider sx={navStyles.divider} />

                            <ListItem
                                button
                                sx={navStyles.mobileListItem}
                                onClick={handleLogoutClick}
                            >
                                <ListItemIcon sx={navStyles.mobileIcon}>
                                    <ExitToAppIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Logout"
                                    primaryTypographyProps={{
                                        color: 'error'
                                    }}
                                />
                            </ListItem>
                        </>
                    ) : (
                        <>
                            <ListItem
                                button
                                sx={navStyles.mobileListItem}
                                onClick={() => navigateTo('/languages')}
                            >
                                <ListItemIcon sx={navStyles.mobileIcon}>
                                    <TranslateIcon />
                                </ListItemIcon>
                                <ListItemText primary="Languages" />
                            </ListItem>

                            <Divider sx={navStyles.divider} />

                            <ListItem
                                button
                                sx={navStyles.mobileListItem}
                                onClick={() => navigateTo('/login')}
                            >
                                <ListItemIcon sx={navStyles.mobileIcon}>
                                    <LoginIcon />
                                </ListItemIcon>
                                <ListItemText primary="Login" />
                            </ListItem>

                            <ListItem
                                button
                                sx={navStyles.mobileListItem}
                                onClick={() => navigateTo('/signup')}
                            >
                                <ListItemIcon sx={navStyles.mobileIcon}>
                                    <AccountCircleIcon />
                                </ListItemIcon>
                                <ListItemText primary="Sign Up" />
                            </ListItem>
                        </>
                    )}
                </List>
            </Drawer>

            <Menu
                anchorEl={profileMenu}
                open={Boolean(profileMenu)}
                onClose={handleProfileMenuClose}
                PaperProps={{
                    sx: navStyles.menu
                }}
            >
                <Box px={2} py={1}>
                    <Typography variant="subtitle2" color="text.primary" fontWeight="medium">
                        {displayUsername}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {userEmail}
                    </Typography>
                </Box>
                <Divider sx={navStyles.divider} />
                <MenuItem
                    sx={navStyles.menuItem}
                    onClick={() => navigateTo('/settings')}
                >
                    <SettingsIcon fontSize="small" />
                    <Typography>Settings</Typography>
                </MenuItem>
                {/* Only show tour option if user is logged in */}
                {token && (
                    <MenuItem
                        sx={navStyles.menuItem}
                        onClick={handleStartTour}
                    >
                        <TourIcon fontSize="small" />
                        <Typography>How to use</Typography>
                    </MenuItem>
                )}
                <Divider sx={navStyles.divider} />
                <MenuItem
                    sx={navStyles.menuItem}
                    onClick={handleLogoutClick}
                >
                    <ExitToAppIcon fontSize="small" color="error" />
                    <Typography color="error">Logout</Typography>
                </MenuItem>
            </Menu>
        </>
    ), [
        toggleMobileMenu, navStyles, handleLogoClick, token, handleProfileMenuOpen,
        getUserInitial, mobileMenu, getLanguageText, navigateTo, hasLanguageSelected,
        currentCourse, getCurrentLanguageCode, getCurrentLanguageFlag, displayUsername,
        handleStartTour, handleLogoutClick, profileMenu, handleProfileMenuClose, userEmail
    ]);

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={navStyles.appBar}
        >
            <Toolbar sx={{ justifyContent: 'space-between' }}>
                {isMobile ? mobileNavbar : desktopNavbar}
            </Toolbar>
        </AppBar>
    );
});

// Add display name for debugging
Navbar.displayName = 'Navbar';

export default Navbar;